/**
 * Extranet application shell (design `app-shell`): the dark sidebar docked on
 * `lg+` (collapsible), the same sidebar in a left `Drawer` below `lg`
 * (opened from the header's menu button), the non-production environment
 * banner, the top bar and the scrolling page area.
 *
 * Pure chrome — not a registered configurable component (the navigation
 * entries inside both sidebars are: `extranet.sidebarMenu`).
 */

import { useCallback, useState, type ReactNode } from 'react';

import { Drawer } from '@components/base/feedback';

import { LAYOUT_TEXT } from './chromeText';
import { ExtranetEnvironmentBanner } from './ExtranetEnvironmentBanner';
import { ExtranetHeader } from './ExtranetHeader';
import { ExtranetSidebar } from './ExtranetSidebar';

interface ExtranetLayoutProps {
  children: ReactNode;
}

export function ExtranetLayout({ children }: Readonly<ExtranetLayoutProps>) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-secondary-50">
      {/* Docked sidebar (lg+). */}
      <ExtranetSidebar
        className="hidden shrink-0 lg:block"
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Only rendered when the active VEOS registry is not production. */}
        <ExtranetEnvironmentBanner />
        <ExtranetHeader onOpenMenu={openMenu} />
        <main className="flex-1 overflow-y-auto" data-testid="extranet-main">
          {children}
        </main>
      </div>

      {/* Mobile / tablet navigation (< lg). */}
      <Drawer open={menuOpen} onClose={closeMenu} side="left" width="sm">
        <Drawer.Header title={LAYOUT_TEXT.drawerTitle} />
        <Drawer.Body className="!p-0">
          <ExtranetSidebar
            className="w-full"
            sidebarClassName="!w-full !border-r-0"
            onNavigate={closeMenu}
          />
        </Drawer.Body>
      </Drawer>
    </div>
  );
}
